import { useCallback, useState } from 'react';
import { useSupplierClaimCandidate } from '@/hooks/useSupplierClaimCandidate';
import type { SupplierClaimCandidate } from '@/lib/supplierClaimCandidate';

/**
 * ENT-6459 (D2, fail-closed-on-unknown): a customer decision (create a workspace, choose a
 * plan) waits until the supplier claim candidate is known.
 * - `checking`: the candidate read is in flight; decisions are disabled. No timer.
 * - `unknown`: the read failed; decisions stay disabled until the user retries or chooses to
 *   continue as a customer for this mount.
 * - `ready`: the answer is known (an offer or none), or the gate does not apply.
 */
export type SupplierClaimDecisionState = 'checking' | 'unknown' | 'ready';

export interface SupplierClaimDecisionGate {
  state: SupplierClaimDecisionState;
  /** The candidate when the answer is known; null otherwise. */
  candidate: SupplierClaimCandidate | null;
  decisionsBlocked: boolean;
  retry: () => void;
  continueAsCustomer: () => void;
}

export function useSupplierClaimDecisionGate({ applies }: { applies: boolean }): SupplierClaimDecisionGate {
  const { candidate, status, refetch } = useSupplierClaimCandidate();
  const [continuedAsCustomer, setContinuedAsCustomer] = useState(false);
  const continueAsCustomer = useCallback(() => setContinuedAsCustomer(true), []);

  let state: SupplierClaimDecisionState = 'ready';
  if (applies && status === 'loading') state = 'checking';
  else if (applies && status === 'error' && !continuedAsCustomer) state = 'unknown';

  return {
    state,
    candidate: applies && status === 'ready' ? candidate : null,
    decisionsBlocked: state !== 'ready',
    retry: refetch,
    continueAsCustomer,
  };
}
